import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  createHsnCode,
  updateHsnCode,
  fetchHsnCodebyId,
} from "../../../services/hsnCodeService";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { handleErrors } from "../../../utils/errorHandler";
import ComponentHeader from "../../Common/OtherElements/ComponentHeader";
import { InputField } from "../../Common/FormElements/InputField";
import { useFormValidation } from "../../../hooks/useFormValidation";
import { usePageLevelAccess } from "../../../hooks/usePageLevelAccess";

export const AddHSNCode = ({
  editMode = false,
  initialData = {},
  onSuccess,
  setSelectedHSNCode,
  setEditMode,
}) => {
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);

  const navigate = useNavigate();
  const PageLevelAccessurl = "hsn-code";
  const { pageAccessData } = usePageLevelAccess(PageLevelAccessurl);
  // ✅ Form validation hook
  const {
    formErrors,
    isFormValid,
    registerField,
    validateAllFields,
    clearErrors,
    touchAllFields,
    getFieldLabel,
  } = useFormValidation();

  // ✅ Create refs for each field
  const hsnRef = useRef(null);
  const taxPercentRef = useRef(null);

  // ✅ Initialize all state properly
  const [formValues, setFormValues] = useState({
    HSN: "",
    TaxPercent: "",
  });

  // ✅ Register all fields
  useEffect(() => {
    registerField("HSN", hsnRef, "HSN Code");
    registerField("TaxPercent", taxPercentRef, "Tax Percent");
  }, [registerField]);

  // Add this useEffect to validate after formValues change
  useEffect(() => {
    // Only validate if form has been touched/submitted before
    if (Object.keys(formErrors).length > 0) {
      const timeoutId = setTimeout(() => {
        validateAllFields();
      }, 100); // Small delay to ensure refs are updated

      return () => clearTimeout(timeoutId);
    }
  }, [formValues, validateAllFields, formErrors]);

  // ✅ Fix page access check with proper dependencies
  useEffect(() => {
    if (pageAccessData) {
      if (!pageAccessData.addAccess || !pageAccessData.viewAccess) {
        navigate("/404-error-page");
      }
    }
  }, [pageAccessData, navigate]);

  useEffect(() => {
    const fetchData = async () => {
      if (editMode && initialData.id) {
        try {
          const data = await fetchHsnCodebyId(initialData.id);
          if (data && data.isSuccess && data.result) {
            setFormValues({
              HSN: data.result.hsn,
              TaxPercent: data.result.taxPercent,
            });
          }
        } catch (error) {
          handleErrors(error);
        }
      } else {
        setFormValues({ HSN: "", TaxPercent: "" });
      }
    };
    fetchData();
  }, [editMode, initialData]);

  const handleInputChange = (e) => {
    const { id, type, value, checked } = e.target;
    const inputValue = type === "checkbox" ? checked : value;

    setFormValues((prev) => {
      const newValues = {
        ...prev,
        [id]: inputValue,
      };
      return newValues;
    });
  };

  // ✅ Reset all form fields
  const resetAllFields = () => {
    const fieldRefs = {
      hsnRef,
      taxPercentRef,
    };
    Object.values(fieldRefs).forEach((ref) => {
      if (ref.current && ref.current.reset) {
        ref.current.reset();
      }
    });
  };

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();

      // ✅ Touch all fields to show validation errors
      touchAllFields();

      // ✅ Validate all fields using refs
      const isValid = validateAllFields();

      if (!isValid) {
        toast.error("Please fix all validation errors before submitting.");
        return;
      }

      try {
        if (editMode) {
          setIsButtonDisabled(true);
          var updateResponseData = await updateHsnCode({
            ...formValues,
            id: initialData.id,
          });
          // ✅ Clear form errors
          clearErrors();
          if (updateResponseData?.data.isSuccess) {
            toast.success(updateResponseData?.data.statusMessage);
          } else {
            toast.error(updateResponseData?.data.statusMessage);
          }
        } else {
          setIsButtonDisabled(true);
          var createResponseData = await createHsnCode(formValues);
          resetAllFields();
          setFormValues({
            HSN: "",
            TaxPercent: "",
          });
          // ✅ Clear form errors
          clearErrors();
          if (createResponseData?.data.isSuccess) {
            toast.success(createResponseData?.data.statusMessage);
          } else {
            toast.error(createResponseData?.data.statusMessage);
          }
        }
        if (onSuccess) onSuccess();
      } catch (error) {
        handleErrors(error);
      } finally {
        setIsButtonDisabled(false);
      }
    },
    [formValues, editMode, initialData, onSuccess, setEditMode]
  );

  const handleAddNewClick = () => {
    setFormValues({
      HSN: "",
      TaxPercent: "",
    });
    resetAllFields();
    setSelectedHSNCode(null);
    setEditMode(false);
  };

  return (
    <>
      <ComponentHeader title="HSN Code" />
      {Object.keys(formErrors).length > 0 && (
        <div className="alert alert-warning mb-3">
          <strong>Please fix the following errors:</strong>
          <ul className="mb-0 mt-2">
            {Object.entries(formErrors).map(([fieldId, error]) => (
              <li key={fieldId}>
                <strong>{getFieldLabel(fieldId)}:</strong> {error}
              </li>
            ))}
          </ul>
        </div>
      )}
      <div className="row">
        <div className="col-xxl-12">
          <div className="card mt-xxl-n5">
            <div className="card-header">
              <h5 className="mb-sm-1 mt-sm-1">
                {editMode ? "Update HSN Code" : "Add HSN Code"}
              </h5>
            </div>

            <div className="card-body p-4">
              <form onSubmit={handleSubmit} method="POST">
                <div className="row">
                  <div className="col-lg-3 col-md-6 col-sm-12">
                    <InputField
                      ref={hsnRef}
                      label="HSN Code"
                      id="HSN"
                      value={formValues.HSN}
                      placeholder="Enter your HSN Code"
                      onChange={handleInputChange}
                      required={true}
                      validationType="numberonly"
                      maxLength={10}
                      minLength={6}
                      showError={true}
                    />
                  </div>

                  <div className="col-lg-3 col-md-6 col-sm-12">
                    <InputField
                      ref={taxPercentRef}
                      label="Tax Percent"
                      id="TaxPercent"
                      value={formValues.TaxPercent}
                      placeholder="Enter your Tax Percentage"
                      onChange={handleInputChange}
                      required={true}
                      validationType="percentage"
                      maxLength={6}
                      showError={true}
                    />
                  </div>
                  <div className="col-lg-12">
                    <div>
                      <button
                        type="submit"
                        className="btn btn-secondary pt-1 pb-1 p-3"
                        disabled={isButtonDisabled || !isFormValid}
                      >
                        {isButtonDisabled
                          ? editMode
                            ? "Updating"
                            : "Saving"
                          : editMode
                          ? "Update"
                          : "Save"}
                      </button>
                      {editMode && (
                        <button
                          type="button"
                          onClick={handleAddNewClick}
                          className="btn btn-danger ms-1 pt-1 pb-1 p-3"
                        >
                          Cancel
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
